import { ELocalForageKeys } from '@aca-new/app/shared/models/enums/localforage-keys.enum';
import { EStorageKeys } from '@aca-new/app/shared/models/enums/storage-keys.enum';
import { StorageService } from '@aca-new/app/shared/services/storage-services/storage.service';
import { Injectable } from '@angular/core';
import localforage from 'localforage';

/**
 * @description
 * Service for managing user-isolated localforage storage
 * Ensures that different users' data is stored separately and cannot access each other's data
 *
 * Key features:
 * - User-specific key generation: `${userId}_${originalKey}`
 * - Automatic user ID resolution from current session
 * - Promise and Observable support
 * - Type-safe operations
 * - Error handling with fallbacks
 */
@Injectable({
  providedIn: 'root',
})
export class UserIsolatedLocalForageService {
  public constructor(private readonly _storageService: StorageService) {}

  /**
   * @description
   * Get current user ID from storage
   * @returns {string} Current user ID or empty string if not found
   */
  public getCurrentUserId(): string {
    return this._storageService.getItem(EStorageKeys.USER_ID);
  }

  public generateUserSpecificKey(originalKey: ELocalForageKeys, userId?: string): string {
    const currentUserId = userId || this.getCurrentUserId();

    return `${currentUserId}_${originalKey}`;
  }

  public async setItem<T = unknown>(key: ELocalForageKeys, value: T, userId?: string): Promise<void> {
    try {
      const userSpecificKey = this.generateUserSpecificKey(key, userId);

      await localforage.setItem(userSpecificKey, value);
    } catch (error) {
      console.error('Error setting user-isolated localforage item:', error);
      throw error;
    }
  }

  public async getItem<T = unknown>(key: ELocalForageKeys, userId?: string): Promise<T | null> {
    try {
      const userSpecificKey = this.generateUserSpecificKey(key, userId);

      return await localforage.getItem<T>(userSpecificKey);
    } catch (error) {
      console.error('Error getting user-isolated localforage item:', error);

      return null;
    }
  }

  /**
   * @description
   * Remove item from user-isolated localforage storage
   * @param {ELocalForageKeys} key - Original localforage key
   * @param {string} userId - User ID (optional, uses current user if not provided)
   * @returns {Promise<void>}
   */
  public async removeItem(key: ELocalForageKeys, userId?: string): Promise<void> {
    try {
      const userSpecificKey = this.generateUserSpecificKey(key, userId);

      await localforage.removeItem(userSpecificKey);
    } catch (error) {
      console.error('Error removing user-isolated localforage item:', error);
      throw error;
    }
  }
}
